import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon, MapPinIcon, ShieldCheckIcon, FileTextIcon, HistoryIcon, CreditCardIcon, GavelIcon, BuildingIcon, AlertCircleIcon, CheckCircleIcon, GlobeIcon, MapIcon, DownloadIcon, PrinterIcon, XIcon, Loader2Icon, CheckIcon, GripHorizontalIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function LandRecordDetailsPage({
  plotId,
  navigateTo
}: {
  plotId: string | null;
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  // CLO Modal State
  const [cloStep, setCloStep] = useState(0); // 0: closed, 1: summary, 2: payment, 2.5: otp, 3: success, 4: certificate
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedCloId, setGeneratedCloId] = useState('');
  // Modal Drag & Resize State
  const [modalPos, setModalPos] = useState({
    x: 0,
    y: 0
  });
  const [modalSize, setModalSize] = useState({
    width: 1024,
    height: 800
  }); // Default max-w-5xl is roughly 1024px
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStart = useRef({
    x: 0,
    y: 0,
    modalX: 0,
    modalY: 0,
    width: 0,
    height: 0
  });
  const plot = samplePlots.find(p => p.id === plotId) || samplePlots[0];
  const tabs = [{
    id: 'overview',
    label: t('tabOverview') || 'Overview',
    icon: FileTextIcon
  }, {
    id: 'mutation',
    label: t('tabMutation') || 'Mutation Info',
    icon: FileTextIcon
  }, {
    id: 'tax',
    label: t('tabTax') || 'LD TAX Info',
    icon: CreditCardIcon
  }, {
    id: 'parcelMap',
    label: t('tabParcelMap') || 'Parcel Map',
    icon: MapIcon
  }, {
    id: 'transferHistory',
    label: t('tabTransferHistory') || 'Transfer History',
    icon: HistoryIcon
  }, {
    id: 'disputes',
    label: t('tabDisputes') || 'Case Info',
    icon: GavelIcon
  }, {
    id: 'mortgages',
    label: t('tabMortgages') || 'Mortgages Info',
    icon: BuildingIcon
  }];
  const handleApplyCLO = () => {
    setCloStep(1);
    // Reset modal size/pos when opening
    setModalPos({
      x: 0,
      y: 0
    });
    setModalSize({
      width: Math.min(window.innerWidth * 0.9, 1024),
      height: Math.min(window.innerHeight * 0.9, 800)
    });
  };
  const handleProcessPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setGeneratedCloId(`CLO-${plot.district.substring(0, 3).toUpperCase()}-2026-NEW-${Math.floor(100000 + Math.random() * 900000)}`);
      setCloStep(3);
    }, 2000);
  };
  const closeCLOModal = () => {
    setCloStep(0);
    setIsProcessing(false);
  };
  // Drag and Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setModalPos({
          x: dragStart.current.modalX + dx,
          y: dragStart.current.modalY + dy
        });
      } else if (isResizing.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setModalSize({
          width: Math.max(400, dragStart.current.width + dx),
          height: Math.max(300, dragStart.current.height + dy)
        });
      }
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
      document.body.style.userSelect = ''; // Restore text selection
    };
    if (cloStep > 0) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [cloStep]);
  const onHeaderMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      modalX: modalPos.x,
      modalY: modalPos.y,
      width: modalSize.width,
      height: modalSize.height
    };
    document.body.style.userSelect = 'none'; // Prevent text selection while dragging
  };
  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isResizing.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      modalX: modalPos.x,
      modalY: modalPos.y,
      width: modalSize.width,
      height: modalSize.height
    };
    document.body.style.userSelect = 'none';
  };
  // OTP State
  const [otp, setOtp] = useState(['5', '8', '3', '1']);
  const otpRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };
  return <div className="min-h-screen bg-[#F8FAFB] flex flex-col font-sans" data-id="element-449">
      {/* Top Government Header Bar */}
      <div className="bg-[#006A4E] text-white py-2 px-4 md:px-8 flex justify-between items-center border-b-4 border-[#DC2626]" data-id="element-450">
        <div className="flex items-center space-x-3" data-id="element-451">
          <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Bangladesh Government Logo" className="w-10 h-10 rounded-full object-contain bg-white border-2 border-[#C9A84C]" data-id="element-452" />
          <div className="flex flex-col" data-id="element-453">
            <span className="text-sm md:text-base font-bold leading-tight" data-id="element-454">
              {t('govName')}
            </span>
            <span className="text-xs md:text-sm text-green-100 leading-tight" data-id="element-455">
              {t('ministryName')}
            </span>
          </div>
        </div>
        <button onClick={toggleLanguage} className="flex items-center space-x-1 text-sm font-medium hover:text-green-200 transition-colors" data-id="element-456">
          <GlobeIcon className="w-4 h-4" data-id="element-457" />
          <span data-id="element-458">{language === 'bn' ? 'English' : 'বাংলা'}</span>
        </button>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-8 space-y-6" data-id="element-459">
        <button onClick={() => navigateTo('mouzaMap')} className="flex items-center text-sm font-medium text-gray-500 hover:text-[#006A4E] transition-colors" data-id="element-460">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-461" />{' '}
          {language === 'bn' ? 'মৌজা মানচিত্রে ফিরে যান' : 'Back to Mouza Map'}
        </button>

        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden" data-id="element-462">
          {plot.dispute && <div className="absolute top-0 left-0 w-full h-1 bg-red-500" data-id="element-463"></div>}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-464">
            <div className="flex items-center space-x-4" data-id="element-465">
              <div className="w-16 h-16 bg-[#006A4E] rounded-xl flex items-center justify-center shadow-inner flex-shrink-0" data-id="element-466">
                <MapPinIcon className="w-8 h-8 text-white" data-id="element-467" />
              </div>
              <div data-id="element-468">
                <div className="flex items-center space-x-2 mb-1" data-id="element-469">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-470">
                    {language === 'bn' ? plot.nameBn : plot.name}
                  </h1>
                  {plot.dispute && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200" data-id="element-471">
                      <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-472" />
                      Disputed
                    </span>}
                </div>
                <p className="text-sm text-gray-500 flex items-center" data-id="element-473">
                  <span className="font-medium text-gray-700 mr-2" data-id="element-474">
                    ID: {plot.id}
                  </span>
                  <span className="mx-2 text-gray-300" data-id="element-475">|</span>
                  {language === 'bn' ? plot.districtBn : plot.district},{' '}
                  {language === 'bn' ? plot.upazilaBn : plot.upazila}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto" data-id="element-476">
              {plot.cloId && <div className="inline-flex items-center px-4 py-2.5 rounded-lg bg-green-50 text-green-800 text-sm font-bold border border-green-200 w-full sm:w-auto justify-center" data-id="element-477">
                  <ShieldCheckIcon className="w-4 h-4 mr-2" data-id="element-478" />
                  {plot.cloId}
                </div>}
              <button onClick={handleApplyCLO} className="inline-flex items-center px-4 py-2.5 rounded-lg bg-[#006A4E] text-white text-sm font-bold hover:bg-[#004D3A] transition-colors shadow-sm w-full sm:w-auto justify-center" data-id="element-479">
                <ShieldCheckIcon className="w-4 h-4 mr-2" data-id="element-480" />
                {t('applyCLO') || 'Apply for CLO Certificate'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[500px]" data-id="element-481">
          {/* Vertical Tabs (Desktop) / Horizontal Scroll (Mobile) */}
          <div className="w-full md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0" data-id="element-482">
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible hide-scrollbar p-2 md:p-4 gap-1" data-id="element-483">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#006A4E] text-white shadow-md' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`} data-id="element-484">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-green-100' : 'text-gray-400'}`} data-id="element-485" />
                    <span data-id="element-486">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-487">
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300" data-id="element-488">
                <div className="space-y-8" data-id="element-489">
                  <div data-id="element-490">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-491">
                      <MapPinIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-492" />
                      {t('locationInfo')}
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4" data-id="element-493">
                      <div className="grid grid-cols-2 gap-4" data-id="element-494">
                        <div data-id="element-495">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-496">
                            {t('district')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-497">
                            {language === 'bn' ? plot.districtBn : plot.district}
                          </p>
                        </div>
                        <div data-id="element-498">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-499">
                            {t('upazila')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-500">
                            {language === 'bn' ? plot.upazilaBn : plot.upazila}
                          </p>
                        </div>
                        <div className="col-span-2" data-id="element-501">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-502">
                            {t('mouza')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-503">
                            {language === 'bn' ? plot.mouzaBn : plot.mouza}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-504">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-505">
                      <FileTextIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-506" />
                      {t('khatianDetails') || 'Khatian Details'}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-507">
                      <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100" data-id="element-508">
                        <p className="text-xs text-blue-600 uppercase tracking-wider mb-1" data-id="element-509">
                          {t('khatian')}
                        </p>
                        <p className="text-xl font-bold text-blue-900" data-id="element-510">
                          {plot.khatian}
                        </p>
                      </div>
                      <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100" data-id="element-511">
                        <p className="text-xs text-purple-600 uppercase tracking-wider mb-1" data-id="element-512">
                          {t('dag')}
                        </p>
                        <p className="text-xl font-bold text-purple-900" data-id="element-513">
                          {plot.dag}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-514">
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-515">
                        <span className="text-gray-500 text-sm" data-id="element-516">
                          {t('surveyType') || 'Survey Type'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-517">
                          {language === 'bn' ? (plot as any).surveyNameBn || 'বিআরএস' : (plot as any).surveyName || 'BRS'}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-518">
                        <span className="text-gray-500 text-sm" data-id="element-519">
                          {t('ownerName') || 'Owner Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-520">
                          {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-521">
                        <span className="text-gray-500 text-sm" data-id="element-522">
                          {t('fatherName') || 'Father Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-523">
                          {language === 'bn' ? (plot as any).fatherNameBn || 'অজ্ঞাত' : (plot as any).fatherName || 'Unknown'}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-524">
                        <span className="text-gray-500 text-sm" data-id="element-525">
                          {t('address') || 'Address'}
                        </span>
                        <span className="font-semibold text-gray-900 text-right" data-id="element-526">
                          {language === 'bn' ? (plot as any).addressBn || `${plot.mouzaBn}, ${plot.upazilaBn}` : (plot as any).address || `${plot.mouza}, ${plot.upazila}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-8" data-id="element-527">
                  <div data-id="element-528">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-529">
                      <ShieldCheckIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-530" />
                      Property Status
                    </h3>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-531">
                      <div className="p-4 flex justify-between items-center" data-id="element-532">
                        <span className="text-gray-600 font-medium" data-id="element-533">
                          {t('landType')}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold capitalize" data-id="element-534">
                          {t(plot.type as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-535">
                        <span className="text-gray-600 font-medium" data-id="element-536">
                          {t('area')}
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-537">
                          {plot.area} Decimals
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-538">
                        <span className="text-gray-600 font-medium" data-id="element-539">
                          {t('ownershipType')}
                        </span>
                        <span className="font-semibold text-gray-900 capitalize" data-id="element-540">
                          {t(plot.ownership as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-541">
                        <span className="text-gray-600 font-medium" data-id="element-542">
                          {t('mutationStatus')}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center ${plot.mutationStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-543">
                          {plot.mutationStatus === 'completed' && <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-544" />}
                          {t(plot.mutationStatus as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-545">
                        <span className="text-gray-600 font-medium" data-id="element-546">
                          {t('taxStatus')}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plot.taxStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-547">
                          {t(plot.taxStatus as any)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* MUTATION INFO TAB - CARD LAYOUT */}
            {activeTab === 'mutation' && <div className="animate-in fade-in duration-300" data-id="element-548">
                <div className="flex justify-between items-center mb-6" data-id="element-549">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-550">
                    {t('tabMutation') || 'Mutation Info'}
                  </h3>
                </div>

                {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="space-y-6" data-id="element-551">
                    {plot.transferHistory.map((record: any, idx: number) => <div key={record.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-552">
                        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-553">
                          <span className="font-bold text-gray-700 flex items-center" data-id="element-554">
                            <FileTextIcon className="w-4 h-4 mr-2 text-[#006A4E]" data-id="element-555" />
                            Record #{idx + 1}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-556">
                            {language === 'bn' ? record.typeBn : record.type}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-557">
                          <div data-id="element-558">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-559">
                              {t('transferDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-560">
                              {record.date}
                            </p>
                          </div>
                          <div data-id="element-561">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-562">
                              {t('transferFrom')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-563">
                              {record.from}
                            </p>
                          </div>
                          <div data-id="element-564">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-565">
                              {t('transferTo')}
                            </p>
                            <p className="font-medium text-[#006A4E]" data-id="element-566">
                              {record.to}
                            </p>
                          </div>
                          <div data-id="element-567">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-568">
                              {t('transferType')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-569">
                              {language === 'bn' ? record.typeBn : record.type}
                            </p>
                          </div>
                          <div data-id="element-570">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-571">
                              {t('mutCaseNo') || 'Case No.'}
                            </p>
                            <p className="font-mono font-medium text-gray-900" data-id="element-572">
                              {record.caseNo || record.dolilNo || record.docRef}
                            </p>
                          </div>
                          <div data-id="element-573">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-574">
                              {t('mutCaseDate') || 'Case Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-575">
                              {record.caseDate || record.dolilDate || record.date}
                            </p>
                          </div>
                          <div data-id="element-576">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-577">
                              {t('approvalDate') || 'Approval Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-578">
                              {record.approvalDate || '-'}
                            </p>
                          </div>
                          <div data-id="element-579">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-580">
                              {t('dcrNo') || 'DCR No.'}
                            </p>
                            <p className="font-mono font-medium text-gray-900" data-id="element-581">
                              {record.dcrNo || '-'}
                            </p>
                          </div>
                          <div data-id="element-582">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-583">
                              {t('dcrDate') || 'DCR Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-584">
                              {record.dcrDate || '-'}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-585">
                    <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-586" />
                    <p className="text-gray-500 font-medium" data-id="element-587">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* LD TAX INFO TAB */}
            {activeTab === 'tax' && <div className="animate-in fade-in duration-300" data-id="element-588">
                <div className="flex justify-between items-center mb-6" data-id="element-589">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-590">
                    {t('ldTaxHistory') || 'LD TAX Info'}
                  </h3>
                </div>

                {plot.taxHistory && plot.taxHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-591">
                    <div className="overflow-x-auto" data-id="element-592">
                      <table className="w-full text-left text-sm" data-id="element-593">
                        <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200" data-id="element-594">
                          <tr data-id="element-595">
                            <th className="px-6 py-4" data-id="element-596">{t('taxYear')}</th>
                            <th className="px-6 py-4" data-id="element-597">{t('amount')}</th>
                            <th className="px-6 py-4" data-id="element-598">{t('status')}</th>
                            <th className="px-6 py-4" data-id="element-599">
                              {t('dhakhilaNo') || 'Dhakhila No.'}
                            </th>
                            <th className="px-6 py-4" data-id="element-600">
                              {t('dhakhilaDate') || 'Dhakhila Date'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100" data-id="element-601">
                          {plot.taxHistory.map((record: any) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-602">
                              <td className="px-6 py-4 font-medium text-gray-900" data-id="element-603">
                                {record.year}
                              </td>
                              <td className="px-6 py-4 font-bold text-gray-700" data-id="element-604">
                                {record.amount}
                              </td>
                              <td className="px-6 py-4" data-id="element-605">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-606">
                                  {t(record.status as any)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-500 font-mono text-xs" data-id="element-607">
                                {record.dhakhilaNo || record.receiptNo}
                              </td>
                              <td className="px-6 py-4 text-gray-500" data-id="element-608">
                                {record.dhakhilaDate || record.date}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-609">
                    <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-610" />
                    <p className="text-gray-500 font-medium" data-id="element-611">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* PARCEL MAP TAB */}
            {activeTab === 'parcelMap' && <div className="animate-in fade-in duration-300" data-id="element-612">
                <div className="flex justify-between items-center mb-6" data-id="element-613">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-614">
                    {t('parcelMap') || 'Parcel Map'}
                  </h3>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-615">
                  <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 bg-white flex justify-center" data-id="element-616">
                    <img src="https://cdn.magicpatterns.com/uploads/ny2k3HFhyHRGZ8XpcGKfEL/Parcel-map-data.jpg" alt="Parcel Map" className="max-w-full h-auto object-contain max-h-[600px]" data-id="element-617" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-618">
                    <div data-id="element-619">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-620">
                        {t('khatian')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-621">
                        {plot.khatian}
                      </p>
                    </div>
                    <div data-id="element-622">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-623">
                        {t('dag')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-624">
                        {plot.dag}
                      </p>
                    </div>
                    <div data-id="element-625">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-626">
                        {t('mouza')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-627">
                        {language === 'bn' ? plot.mouzaBn : plot.mouza}
                      </p>
                    </div>
                    <div data-id="element-628">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-629">
                        {t('area')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-630">
                        {plot.area} Decimals
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {/* TRANSFER HISTORY TAB */}
            {activeTab === 'transferHistory' && <div className="animate-in fade-in duration-300" data-id="element-631">
                <div className="flex justify-between items-center mb-6" data-id="element-632">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-633">
                    {t('tabTransferHistory') || 'Transfer History'}
                  </h3>
                </div>

                {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-634">
                    <div className="bg-[#4CAF50] text-white text-center py-3 font-bold text-lg" data-id="element-635">
                      {t('transferStatement') || 'হস্তান্তর বৃত্তান্ত'}
                    </div>
                    <div className="overflow-x-auto" data-id="element-636">
                      <table className="w-full text-center text-sm border-collapse" data-id="element-637">
                        <thead className="bg-gray-50 text-gray-800 font-medium" data-id="element-638">
                          <tr data-id="element-639">
                            <th className="px-4 py-3 border border-gray-200" data-id="element-640">
                              {t('prevKhatianNo') || 'আগত খতিয়ান নং'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-641">
                              {t('dag') || 'দাগ নং'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-642">
                              {t('dagLandArea') || 'দাগে জমির পরিমান (একর)'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-643">
                              {t('khatianLandArea') || 'খতিয়ানে জমির পরিমাণ (একর)'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-644">
                              {t('transferredLandArea') || 'হস্তান্তরিত জমির পরিমাণ (একর)'}
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-645">
                          {plot.transferHistory.map((record: any, idx: number) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-646">
                                <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900" data-id="element-647">
                                  {record.prevKhatianNo || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-648">
                                  {record.dagNo || plot.dag}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-649">
                                  {record.dagLandArea || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-650">
                                  {record.khatianLandArea || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 font-bold text-gray-900" data-id="element-651">
                                  {record.transferredLandArea || '-'}
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <div className="border-t border-gray-200" data-id="element-652">
                      <div className="flex border-b border-gray-200" data-id="element-653">
                        <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-654">
                          {t('ownershipSource') || 'মালিকানা সূত্র'}
                        </div>
                        <div className="w-2/3 md:w-3/4 p-4 text-gray-900 font-medium flex items-center" data-id="element-655">
                          {language === 'bn' ? (plot.transferHistory[0] as any).ownershipSourceBn || `${plot.transferHistory[0].typeBn} সূত্রেঃ` : (plot.transferHistory[0] as any).ownershipSource || `By ${plot.transferHistory[0].type}:`}
                        </div>
                      </div>
                      <div className="flex" data-id="element-656">
                        <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-657">
                          {t('descriptionLabel') || 'বিবরণ'}
                        </div>
                        <div className="w-2/3 md:w-3/4 p-4 text-gray-500 tracking-widest flex items-center" data-id="element-658">
                          ------------------------
                        </div>
                      </div>
                    </div>
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-659">
                    <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-660" />
                    <p className="text-gray-500 font-medium" data-id="element-661">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* CASE INFO TAB */}
            {activeTab === 'disputes' && <div className="animate-in fade-in duration-300" data-id="element-662">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-663">
                  {t('caseInfo') || 'Case Info'}
                </h3>

                {plot.litigations && plot.litigations.length > 0 ? <div className="space-y-4" data-id="element-664">
                    {plot.litigations.map((lit: any) => <div key={lit.id} className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden" data-id="element-665">
                        <div className="bg-red-50 px-6 py-3 border-b border-red-100 flex justify-between items-center" data-id="element-666">
                          <div className="flex items-center space-x-2" data-id="element-667">
                            <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-668" />
                            <span className="font-bold text-red-900" data-id="element-669">
                              {lit.caseNo}
                            </span>
                          </div>
                          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold" data-id="element-670">
                            {language === 'bn' ? lit.statusBn : lit.status}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-671">
                          <div data-id="element-672">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-673">
                              {t('court')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-674">
                              {language === 'bn' ? lit.courtBn : lit.court}
                            </p>
                          </div>
                          <div data-id="element-675">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-676">
                              {t('dateFiled')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-677">
                              {lit.dateFiled}
                            </p>
                          </div>

                          {lit.nextHearing && lit.nextHearing !== '—' && <div data-id="element-678">
                              <p className="text-sm text-gray-500 mb-1" data-id="element-679">
                                {language === 'bn' ? 'পরবর্তী শুনানির তারিখ' : 'Next Hearing'}
                              </p>
                              <p className="font-medium text-amber-600" data-id="element-680">
                                {lit.nextHearing}
                              </p>
                            </div>}

                          {lit.result && <div className="col-span-1 md:col-span-2" data-id="element-681">
                              <p className="text-sm text-gray-500 mb-1" data-id="element-682">
                                {language === 'bn' ? 'ফলাফল' : 'Result'}
                              </p>
                              <p className="font-medium text-green-700" data-id="element-683">
                                {language === 'bn' && lit.resultBn ? lit.resultBn : lit.result}
                              </p>
                            </div>}

                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-684">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-685">
                              {t('plaintiff')}
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-686">
                              {language === 'bn' ? lit.plaintiffBn : lit.plaintiff}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-687">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-688">
                              {t('defendant')}
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-689">
                              {language === 'bn' ? lit.defendantBn : lit.defendant}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-green-50 rounded-xl border border-green-200" data-id="element-690">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-691">
                      <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-692" />
                    </div>
                    <h4 className="text-lg font-bold text-green-900 mb-2" data-id="element-693">
                      Clear Title
                    </h4>
                    <p className="text-green-700" data-id="element-694">
                      No active litigations or disputes found for this property.
                    </p>
                  </div>}
              </div>}

            {/* MORTGAGES INFO TAB */}
            {activeTab === 'mortgages' && <div className="animate-in fade-in duration-300" data-id="element-695">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-696">
                  {t('mortgagesInfo') || 'Mortgages Info'}
                </h3>

                {plot.mortgages && plot.mortgages.length > 0 ? <div className="space-y-4" data-id="element-697">
                    {plot.mortgages.map((mortgage: any) => <div key={mortgage.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-698">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-699">
                          <div className="flex items-center space-x-3" data-id="element-700">
                            <BuildingIcon className="w-5 h-5 text-gray-500" data-id="element-701" />
                            <span className="font-bold text-gray-900 text-lg" data-id="element-702">
                              {language === 'bn' ? mortgage.bankBn : mortgage.bank}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${mortgage.status === 'active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-703">
                            {t(mortgage.status as any)}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-704">
                          <div data-id="element-705">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-706">
                              {t('loanAmount')}
                            </p>
                            <p className="text-xl font-bold text-gray-900" data-id="element-707">
                              {mortgage.amount}
                            </p>
                          </div>
                          <div data-id="element-708">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-709">
                              {t('mortgageDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-710">
                              {mortgage.date}
                            </p>
                          </div>
                          <div data-id="element-711">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-712">
                              {t('releaseDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-713">
                              {mortgage.releaseDate}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-714">
                    <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-715" />
                    <p className="text-gray-500 font-medium" data-id="element-716">
                      No mortgage records found.
                    </p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>

      {/* CLO APPLICATION MODAL */}
      {cloStep > 0 && <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" data-id="element-717">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col relative" style={{
        width: `${modalSize.width}px`,
        height: `${modalSize.height}px`,
        transform: `translate(${modalPos.x}px, ${modalPos.y}px)`,
        maxWidth: '100vw',
        maxHeight: '95vh',
        transition: isDragging.current || isResizing.current ? 'none' : 'transform 0.2s ease-out'
      }} data-id="element-718">
            {/* Modal Header (Draggable) */}
            <div className="bg-[#006A4E] px-6 py-4 flex justify-between items-center text-white flex-shrink-0 cursor-move select-none" onMouseDown={onHeaderMouseDown} data-id="element-719">
              <h2 className="text-xl font-bold flex items-center" data-id="element-720">
                <ShieldCheckIcon className="w-6 h-6 mr-2 text-[#C9A84C]" data-id="element-721" />
                {cloStep === 4 ? t('viewCLOCertificate') || 'CLO Certificate' : t('applyCLO') || 'Apply for CLO Certificate'}
              </h2>
              <button onClick={e => {
            e.stopPropagation();
            closeCLOModal();
          }} className="text-green-100 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10 cursor-pointer" data-id="element-722">
                <XIcon className="w-6 h-6" data-id="element-723" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 md:p-8 overflow-y-auto flex-1 relative" data-id="element-724">
              {/* Step 1: Summary */}
              {cloStep === 1 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-3xl mx-auto" data-id="element-725">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start" data-id="element-726">
                    <ShieldCheckIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-727" />
                    <p className="text-sm text-blue-800" data-id="element-728">
                      {language === 'bn' ? 'আপনি এই প্লটের জন্য একটি ডিজিটাল সার্টিফিকেট অফ ল্যান্ড ওনারশিপ (CLO) তৈরি করতে যাচ্ছেন। এটি একটি আইনগতভাবে বৈধ ডিজিটাল দলিল।' : 'You are about to generate a digital Certificate of Land Ownership (CLO) for this plot. This is a legally valid digital document.'}
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-729">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-gray-700" data-id="element-730">
                      {t('khatianDetails') || 'Khatian Details'}
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-y-4 gap-x-6 text-sm" data-id="element-731">
                      <div data-id="element-732">
                        <span className="text-gray-500 block mb-1" data-id="element-733">
                          {t('district')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-734">
                          {language === 'bn' ? plot.districtBn : plot.district}
                        </span>
                      </div>
                      <div data-id="element-735">
                        <span className="text-gray-500 block mb-1" data-id="element-736">
                          {t('upazila')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-737">
                          {language === 'bn' ? plot.upazilaBn : plot.upazila}
                        </span>
                      </div>
                      <div data-id="element-738">
                        <span className="text-gray-500 block mb-1" data-id="element-739">
                          {t('mouza')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-740">
                          {language === 'bn' ? plot.mouzaBn : plot.mouza}
                        </span>
                      </div>
                      <div data-id="element-741">
                        <span className="text-gray-500 block mb-1" data-id="element-742">
                          {t('khatian')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-743">
                          {plot.khatian}
                        </span>
                      </div>
                      <div className="col-span-2" data-id="element-744">
                        <span className="text-gray-500 block mb-1" data-id="element-745">
                          {t('ownerName') || 'Owner Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-746">
                          {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-747">
                    <span className="font-bold text-gray-700" data-id="element-748">
                      {t('cloFee') || 'CLO Generation Fee'}
                    </span>
                    <span className="text-2xl font-bold text-[#006A4E]" data-id="element-749">
                      ৳ 500
                    </span>
                  </div>

                  <button onClick={() => setCloStep(2)} className="w-full py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md" data-id="element-750">
                    {t('paymentProcessing') || 'Proceed to Payment'}
                  </button>
                </div>}

              {/* Step 2: Payment Gateway */}
              {cloStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-md mx-auto mt-10" data-id="element-751">
                  <div className="text-center mb-6" data-id="element-752">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-753">
                      Secure Payment Gateway
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-754">
                      Enter your card details to pay ৳ 500
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-755">
                    <div data-id="element-756">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-757">
                        Card Number
                      </label>
                      <div className="relative" data-id="element-758">
                        <CreditCardIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-759" />
                        <input type="text" defaultValue="4111 1111 1111 1111" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-760" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-761">
                      <div data-id="element-762">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-763">
                          Expiry Date
                        </label>
                        <input type="text" defaultValue="12/28" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-764" />
                      </div>
                      <div data-id="element-765">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-766">
                          CVV
                        </label>
                        <input type="password" defaultValue="123" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-767" />
                      </div>
                    </div>

                    <button onClick={() => setCloStep(2.5)} className="w-full py-3.5 mt-4 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md flex justify-center items-center" data-id="element-768">
                      {`${t('payNow') || 'Pay Now'} ৳ 500`}
                    </button>
                    <button onClick={() => setCloStep(1)} className="w-full py-2 text-gray-500 font-medium hover:text-gray-700 text-sm" data-id="element-769">
                      Cancel and go back
                    </button>
                  </div>
                </div>}

              {/* Step 2.5: OTP Verification */}
              {cloStep === 2.5 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-md mx-auto mt-10" data-id="element-770">
                  <div className="text-center mb-6" data-id="element-771">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-772">
                      OTP Verification
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-773">
                      An OTP has been sent to your registered mobile number{' '}
                      <br data-id="element-774" />
                      <span className="font-bold text-gray-700" data-id="element-775">
                        +880171****678
                      </span>
                    </p>
                  </div>

                  <div className="space-y-6" data-id="element-776">
                    <div className="flex justify-center space-x-4" data-id="element-777">
                      {otp.map((digit, index) => <input key={index} ref={otpRefs[index]} type="text" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-[#006A4E] focus:ring-2 focus:ring-[#006A4E]/20 transition-colors" data-id="element-778" />)}
                    </div>

                    <button onClick={handleProcessPayment} disabled={isProcessing || otp.some(d => !d)} className="w-full py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md flex justify-center items-center disabled:opacity-70" data-id="element-779">
                      {isProcessing ? <>
                          <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-780" />
                          Processing...
                        </> : 'Verify OTP'}
                    </button>

                    <div className="text-center space-y-3" data-id="element-781">
                      <button className="text-[#006A4E] font-medium text-sm hover:underline" data-id="element-782">
                        Resend OTP
                      </button>
                      <div className="block" data-id="element-783">
                        <button onClick={() => setCloStep(2)} disabled={isProcessing} className="text-gray-500 font-medium hover:text-gray-700 text-sm" data-id="element-784">
                          Go Back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Step 3: Success */}
              {cloStep === 3 && <div className="text-center space-y-6 py-16 animate-in zoom-in-95" data-id="element-785">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-786">
                    <CheckIcon className="w-12 h-12 text-green-600" data-id="element-787" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900" data-id="element-788">
                    {t('paymentSuccess') || 'Payment Successful!'}
                  </h3>
                  <p className="text-gray-500 max-w-sm mx-auto text-lg" data-id="element-789">
                    Your payment of ৳ 500 has been processed successfully. Your
                    CLO certificate is now ready.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg inline-block text-sm text-gray-600 mb-4" data-id="element-790">
                    Transaction ID:{' '}
                    <span className="font-mono font-bold text-gray-900" data-id="element-791">
                      TXN-{Math.floor(Math.random() * 100000000)}
                    </span>
                  </div>
                  <div data-id="element-792">
                    <button onClick={() => setCloStep(4)} className="px-8 py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md inline-flex items-center" data-id="element-793">
                      <FileTextIcon className="w-5 h-5 mr-2" data-id="element-794" />
                      {t('viewCLOCertificate') || 'View CLO Certificate'}
                    </button>
                  </div>
                </div>}

              {/* Step 4: Certificate View */}
              {cloStep === 4 && <div className="animate-in fade-in h-full flex flex-col" data-id="element-795">
                  <div className="flex justify-end mb-4 space-x-3 flex-shrink-0" data-id="element-796">
                    <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors" data-id="element-797">
                      <DownloadIcon className="w-4 h-4 mr-2" data-id="element-798" />
                      Download PDF
                    </button>
                    <button className="flex items-center px-4 py-2 bg-[#006A4E] text-white rounded-lg hover:bg-[#004D3A] font-medium text-sm transition-colors shadow-sm" data-id="element-799">
                      <PrinterIcon className="w-4 h-4 mr-2" data-id="element-800" />
                      Print
                    </button>
                  </div>

                  {/* The Certificate */}
                  <div className="border-[12px] border-[#006A4E] p-1 bg-white relative mx-auto w-full max-w-4xl shadow-xl" data-id="element-801">
                    <div className="border-[4px] border-[#C9A84C] p-8 md:p-12 relative overflow-hidden" data-id="element-802">
                      {/* Watermark */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none" data-id="element-803">
                        <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Watermark" className="w-96 h-96 object-contain" data-id="element-804" />
                      </div>

                      <div className="relative z-10" data-id="element-805">
                        <div className="flex justify-between items-start mb-8" data-id="element-806">
                          <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Gov Logo" className="w-20 h-20 object-contain" data-id="element-807" />
                          <div className="text-right" data-id="element-808">
                            <p className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-1" data-id="element-809">
                              Government of Bangladesh
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-810">
                              Ministry of Land
                            </p>
                          </div>
                        </div>

                        <div className="text-center mb-10" data-id="element-811">
                          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#006A4E] mb-2" data-id="element-812">
                            Certificate of Land Ownership
                          </h2>
                          <p className="text-gray-500 tracking-widest uppercase text-sm font-semibold" data-id="element-813">
                            Digital Land Record System
                          </p>
                        </div>

                        <div className="flex justify-between items-end border-b-2 border-gray-200 pb-4 mb-8" data-id="element-814">
                          <div data-id="element-815">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-816">
                              Certificate No.
                            </p>
                            <p className="font-mono font-bold text-lg text-gray-900" data-id="element-817">
                              {generatedCloId}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-818">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-819">
                              Date of Issue
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-820">
                              {new Date().toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-8 mb-12" data-id="element-821">
                          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-822">
                            This is to certify that{' '}
                            <span className="font-bold text-gray-900 border-b border-gray-400 px-2" data-id="element-823">
                              {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                            </span>{' '}
                            is the registered owner of the land parcel described
                            below, as per the digital land records of the
                            Government of Bangladesh.
                          </p>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-824">
                            <div data-id="element-825">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-826">
                                District
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-827">
                                {language === 'bn' ? plot.districtBn : plot.district}
                              </p>
                            </div>
                            <div data-id="element-828">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-829">
                                Upazila
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-830">
                                {language === 'bn' ? plot.upazilaBn : plot.upazila}
                              </p>
                            </div>
                            <div data-id="element-831">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-832">
                                Mouza
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-833">
                                {language === 'bn' ? plot.mouzaBn : plot.mouza}
                              </p>
                            </div>
                            <div data-id="element-834">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-835">
                                Land Type
                              </p>
                              <p className="font-bold text-gray-900 capitalize" data-id="element-836">
                                {t(plot.type as any)}
                              </p>
                            </div>
                            <div data-id="element-837">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-838">
                                Khatian No.
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-839">
                                {plot.khatian}
                              </p>
                            </div>
                            <div data-id="element-840">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-841">
                                Dag No.
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-842">
                                {plot.dag}
                              </p>
                            </div>
                            <div className="col-span-2" data-id="element-843">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-844">
                                Total Area
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-845">
                                {plot.area} Decimals
                              </p>
                            </div>
                          </div>

                          {/* New Sections: Tax, Case, Mortgage */}
                          <div className="space-y-6" data-id="element-846">
                            {/* LD TAX Clearance */}
                            <div data-id="element-847">
                              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-848">
                                LD TAX Payment Status
                              </h4>
                              {plot.taxHistory && plot.taxHistory.length > 0 ? <div className={`p-4 rounded-lg border ${plot.taxHistory[0].status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-849">
                                  {plot.taxHistory[0].status === 'paid' ? <div className="flex items-center text-green-800 font-medium mb-2" data-id="element-850">
                                      <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-851" />
                                      All land development taxes are cleared
                                    </div> : <div className="flex items-center text-red-800 font-medium mb-2" data-id="element-852">
                                      <AlertCircleIcon className="w-5 h-5 mr-2" data-id="element-853" />
                                      Tax payment pending for{' '}
                                      {plot.taxHistory[0].year}
                                    </div>}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3" data-id="element-854">
                                    <div data-id="element-855">
                                      <span className="text-gray-500 block" data-id="element-856">
                                        Latest Year
                                      </span>
                                      <span className="font-semibold text-gray-900" data-id="element-857">
                                        {plot.taxHistory[0].year}
                                      </span>
                                    </div>
                                    <div data-id="element-858">
                                      <span className="text-gray-500 block" data-id="element-859">
                                        Amount
                                      </span>
                                      <span className="font-semibold text-gray-900" data-id="element-860">
                                        {plot.taxHistory[0].amount}
                                      </span>
                                    </div>
                                    <div data-id="element-861">
                                      <span className="text-gray-500 block" data-id="element-862">
                                        Status
                                      </span>
                                      <span className={`font-semibold ${plot.taxHistory[0].status === 'paid' ? 'text-green-700' : 'text-red-700'}`} data-id="element-863">
                                        {plot.taxHistory[0].status.toUpperCase()}
                                      </span>
                                    </div>
                                    <div data-id="element-864">
                                      <span className="text-gray-500 block" data-id="element-865">
                                        Dhakhila No.
                                      </span>
                                      <span className="font-mono font-semibold text-gray-900" data-id="element-866">
                                        {plot.taxHistory[0].dhakhilaNo || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </div> : <p className="text-sm text-gray-500 italic" data-id="element-867">
                                  No tax records available.
                                </p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-868">
                              {/* Litigation Status */}
                              <div data-id="element-869">
                                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-870">
                                  Litigation Status
                                </h4>
                                {plot.litigations && plot.litigations.length > 0 ? <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg" data-id="element-871">
                                    <div className="flex items-start text-amber-800" data-id="element-872">
                                      <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-873" />
                                      <div data-id="element-874">
                                        <p className="font-bold mb-1" data-id="element-875">
                                          Active Litigation Found
                                        </p>
                                        <p className="text-sm" data-id="element-876">
                                          Case No:{' '}
                                          <span className="font-mono" data-id="element-877">
                                            {plot.litigations[0].caseNo}
                                          </span>
                                        </p>
                                        <p className="text-sm" data-id="element-878">
                                          Court:{' '}
                                          {language === 'bn' ? plot.litigations[0].courtBn : plot.litigations[0].court}
                                        </p>
                                      </div>
                                    </div>
                                  </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-879">
                                    <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-880" />
                                    <span className="font-medium text-sm" data-id="element-881">
                                      No active litigations or disputes.
                                    </span>
                                  </div>}
                              </div>

                              {/* Mortgage Status */}
                              <div data-id="element-882">
                                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-883">
                                  Mortgage Information
                                </h4>
                                {plot.mortgages && plot.mortgages.some((m: any) => m.status === 'active') ? <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg" data-id="element-884">
                                    <div className="flex items-start text-blue-800" data-id="element-885">
                                      <BuildingIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-886" />
                                      <div data-id="element-887">
                                        <p className="font-bold mb-1" data-id="element-888">
                                          Active Mortgage Found
                                        </p>
                                        <p className="text-sm" data-id="element-889">
                                          Bank:{' '}
                                          {language === 'bn' ? plot.mortgages.find((m: any) => m.status === 'active').bankBn : plot.mortgages.find((m: any) => m.status === 'active').bank}
                                        </p>
                                        <p className="text-sm" data-id="element-890">
                                          Amount:{' '}
                                          {plot.mortgages.find((m: any) => m.status === 'active').amount}
                                        </p>
                                      </div>
                                    </div>
                                  </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-891">
                                    <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-892" />
                                    <span className="font-medium text-sm" data-id="element-893">
                                      No active mortgages.
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-end mt-16" data-id="element-894">
                          <div className="w-28 h-28 bg-white border-2 border-gray-200 p-1 flex items-center justify-center" data-id="element-895">
                            {/* Real Sample QR Code */}
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://land.gov.bd/verify/CLO-2026-99812" alt="QR Code" className="w-full h-full object-contain opacity-90" data-id="element-896" />
                          </div>
                          <div className="text-center" data-id="element-897">
                            <div className="w-48 border-b border-gray-400 mb-2" data-id="element-898"></div>
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest" data-id="element-899">
                              Digital Signature
                            </p>
                            <p className="text-xs text-gray-500 mt-1" data-id="element-900">
                              Ministry of Land
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Resize Handle */}
            <div className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize flex items-end justify-end p-1 text-gray-400 hover:text-gray-600 z-50" onMouseDown={onResizeMouseDown} data-id="element-901">
              <GripHorizontalIcon className="w-4 h-4 transform -rotate-45" data-id="element-902" />
            </div>
          </div>
        </div>}
    </div>;
}